import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useStores } from '@/hooks/use-stores';
import { useIsMobile } from '@/hooks/useIsMobile';
import PublishInsightModal, { PublishInsightFormValues } from '@/comp/Chatbot/PublishInsightModal';
import AssistantTopBar from './AssistantTopBar';
import InsightPanel from './InsightPanel';
import ChatConversation from './ChatConversation';
import MainAssistantPage from './MainAssistantPage';
import { InsightStatus } from '@/constants/enums';
import { useConversationManagement } from './hooks/useConversationManagement';
import { useAISocket } from './hooks/useAISocket';
import { useResponsivePanel } from './hooks/useResponsivePanel';
import { useInsights } from './hooks/useInsights';
import SalesmateBGIcon from '@/comp/common/icons/SalesmateBG';
import * as S from './AIAssistantPage.styles';

const AIAssistantPage: React.FC = observer(() => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const backgroundIconRef = useRef<HTMLDivElement>(null);

  const { chatbotStore, insightsStore, stationStore, devicesStore } = useStores();
  const { isMobile } = useIsMobile();

  const activeTab = 'available'; // Tab selection removed, always show available insights
  const [publishModalVisible, setPublishModalVisible] = useState<boolean>(false);
  const [selectedInsight, setSelectedInsight] = useState<any | null>(null);

  const { isCollapsed: isInsightsPanelCollapsed, toggleCollapse: toggleInsightsPanel } = useResponsivePanel();
  const { ensureConversation, createNewConversation, hasCurrentConversation } = useConversationManagement({
    chatbotStore,
    stationStore,
  });
  const {
    availableStationInsights,
    archivedStationInsights,
    isLoading: insightsLoading,
    isUpdating: insightsUpdating,
    availableInsights,
    archivedInsights,
  } = useInsights({ insightsStore, stationStore, devicesStore });

  useAISocket({ chatbotStore, stationStore });

  // Sync background with scroll - background scrolls in same direction as content
  useEffect(() => {
    const updateBackgroundPosition = () => {
      if (!backgroundIconRef.current) return;

      // Find the scrollable content wrapper
      const scrollableWrapper = document.querySelector('[data-scrollable-content]') as HTMLElement;
      if (scrollableWrapper) {
        const scrollY = scrollableWrapper.scrollTop;
        // Background should move in OPPOSITE direction of scroll (parallax effect)
        // When content scrolls down (scrollY increases), background moves up (negative translateY)
        // With position: fixed, translateY moves relative to viewport
        backgroundIconRef.current.style.transform = `translateY(${-scrollY}px)`;
      }
    };

    const scrollHandler = () => {
      requestAnimationFrame(updateBackgroundPosition);
    };

    // Listen to scroll events from ScrollableContentWrapper
    const scrollableWrapper = document.querySelector('[data-scrollable-content]') as HTMLElement;
    if (scrollableWrapper) {
      scrollableWrapper.addEventListener('scroll', scrollHandler, { passive: true });
    }

    // Initial position
    updateBackgroundPosition();

    return () => {
      if (scrollableWrapper) {
        scrollableWrapper.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [hasCurrentConversation]);

  const handleSend = (message: string) => {
    chatbotStore.sendMessage(message);
  };

  const handlePromptClick = (prompt: string) => {
    ensureConversation();
    chatbotStore.sendPredefinedMessage(prompt);
  };

  const handlePublishClick = (insightId: string) => {
    // Search through all insights to find the clicked one
    const allInsights = [
      ...(availableInsights || []),
      ...(archivedInsights || []),
      ...(insightsStore?.publishedInsightsList || []),
      ...(insightsStore?.draftInsightsList || []),
    ];

    const insight = allInsights.find((i: any) => i.id === insightId);

    if (insight) {
      setSelectedInsight(insight);
      setPublishModalVisible(true);
    }
  };

  const handlePublish = async (values: PublishInsightFormValues) => {
    if (!selectedInsight || !insightsStore) return;

    try {
      const insightId = selectedInsight.id;
      await insightsStore.updateAssistantInsight(insightId, {
        name: values.name,
        deviceId: values.deviceId,
        description: values.description,
        monetizedAnnualValue: values.value,
        valueType: values.valueType,
        status: InsightStatus.Open,
      });
      setPublishModalVisible(false);
      setSelectedInsight(null);
    } catch (error) {
      console.error('Failed to publish insight:', error);
    }
  };

  const handleInsightClick = (insightId: string) => {
    // Search through all insights to find the clicked one
    const allInsights = [
      ...(availableInsights || []),
      ...(archivedInsights || []),
      ...(insightsStore?.publishedInsightsList || []),
      ...(insightsStore?.draftInsightsList || []),
    ];

    const insight = allInsights.find((i: any) => i.id === insightId);

    if (!insight) {
      return;
    }

    // If we're in a conversation, add to it; otherwise create a new one
    ensureConversation();
    chatbotStore.sendPredefinedMessage(t('ShowInsightDetails', { name: insight.name }), 'insight', insightId);
  };

  const handleNewConversation = () => {
    createNewConversation();
  };

  const handlePromptClickFromMain = (prompt: string) => {
    createNewConversation();
    chatbotStore.sendPredefinedMessage(prompt);
  };

  const handleBackClick = () => {
    chatbotStore.setCurrentConversation(null);
    // Remove conversationId from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('conversationId');
    setSearchParams(newSearchParams, { replace: true });
    // Clear from localStorage
    localStorage.removeItem('ai_assistant_current_conversation');
  };

  // Show main page if no current conversation
  // Check chatbotStore.currentConversationId directly to avoid flash of empty conversation
  if (!chatbotStore.currentConversationId) {
    return (
      <S.ScaleWrapper>
        <S.BackgroundIcon ref={backgroundIconRef}>
          <SalesmateBGIcon />
        </S.BackgroundIcon>
        <S.PageContainer>
          <AssistantTopBar />
          <MainAssistantPage
            onNewConversation={handleNewConversation}
            onInsightClick={handleInsightClick}
            onPromptClick={handlePromptClickFromMain}
            onReviewPublish={handlePublishClick}
            onEditInsight={handlePublishClick}
          />
          <PublishInsightModal
            visible={publishModalVisible}
            insight={selectedInsight}
            onCancel={() => {
              setPublishModalVisible(false);
              setSelectedInsight(null);
            }}
            onPublish={handlePublish}
            isLoading={insightsUpdating}
          />
        </S.PageContainer>
      </S.ScaleWrapper>
    );
  }

  // Show conversation view when there's a current conversation
  return (
    <S.ScaleWrapper>
      <S.BackgroundIcon ref={backgroundIconRef}>
        <SalesmateBGIcon />
      </S.BackgroundIcon>
      <S.PageContainer>
        <AssistantTopBar />

        <S.MainContent>
          <S.BackButtonContainer>
            <S.BackButton onClick={handleBackClick}>
              <ArrowLeftOutlined />
            </S.BackButton>
          </S.BackButtonContainer>
          <S.ChatPanel $isEmpty={chatbotStore.currentMessages.length === 0} $isCollapsed={isInsightsPanelCollapsed}>
            <ChatConversation
              onSend={handleSend}
              onPromptClick={handlePromptClick}
              onPublishClick={handlePublishClick}
            />
          </S.ChatPanel>

          <S.InsightPanelWrapper $isCollapsed={isInsightsPanelCollapsed} $isMobile={isMobile}>
            <InsightPanel
              isCollapsed={isInsightsPanelCollapsed}
              isMobile={isMobile}
              activeTab={activeTab}
              availableInsights={availableStationInsights}
              archivedInsights={archivedStationInsights}
              isLoading={insightsLoading}
              onInsightClick={handleInsightClick}
              onToggleCollapse={toggleInsightsPanel}
              disabled={chatbotStore.isLoading || chatbotStore.isStreaming}
            />
          </S.InsightPanelWrapper>
        </S.MainContent>

        <PublishInsightModal
          visible={publishModalVisible}
          insight={selectedInsight}
          onCancel={() => {
            setPublishModalVisible(false);
            setSelectedInsight(null);
          }}
          onPublish={handlePublish}
          isLoading={insightsUpdating}
        />
      </S.PageContainer>
    </S.ScaleWrapper>
  );
});

AIAssistantPage.displayName = 'AIAssistantPage';

export default AIAssistantPage;
